import { useEffect, useMemo, useState } from "react";
import { CircularProgress, useTheme } from "@mui/material";
import { FormPropertyProps } from "../../FormProperty";
import { FoodsCheckbox } from "@foodpilot/foods";
import {
    getPropertyDefaultOptions,
    getPropertyGenericOptions,
    getOptionValues,
    getOptionCodes,
    findOptionsByCodes,
} from "src/utils/dataListOptions";
import { getDataListOptionsWithOverrides } from "src/utils/ssq";
import { usePropertyContext } from "../../context/usePropertyContext";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import { FieldValueLayout } from "../FieldValueLayout";

export const Checkbox = (props: FormPropertyProps) => {
    const theme = useTheme();

    const { post, getElement, onUpdate, canEdit, dataListValueOverrides } = usePropertyContext();
    const { property, position, showSubscore, dimensionValue } = props;
    const element = getElement(property.id, position);

    const options = useMemo(
        () => getDataListOptionsWithOverrides(property.dataList, dataListValueOverrides),
        [property.dataList, dataListValueOverrides],
    );

    const propertyDefaultOptions = getPropertyDefaultOptions(property, options);
    const defaultValueLabel =
        propertyDefaultOptions.length === 0 ? null : propertyDefaultOptions.map((option) => option.label).join("; ");

    const propertyGenericOptions = getPropertyGenericOptions(property, options);
    const genericValueLabel =
        propertyGenericOptions.length === 0 ? null : propertyGenericOptions.map((option) => option.label).join("; ");

    // Initialize with either the element value or default value
    const initialValue = findOptionsByCodes(options, element?.dataListOptionCodes ?? []);

    const [value, setValue] = useState<DataListOption[] | undefined>(initialValue);
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);

    useEffect(() => {
        if (initialValue !== undefined) return;

        setValue(initialValue);
    }, [initialValue]);

    if (!post) return <CircularProgress />;

    const updatePropertyValue = (value?: DataListOption[], isGenericValue?: boolean) => {
        onUpdate({
            postId: post.id,
            propertyId: Number(property.id),
            value: value ? getOptionValues(value) : "_empty_",
            position: Number(props.position ?? 0),
            isGenericValue: isGenericValue,
            dataListOptionCodes: value ? getOptionCodes(value) : [],
        });
    };

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValueLabel,
                genericValue: genericValueLabel,
                isGenericValueSelected: genericValueSelected,
                dimensionValue,
                forceHideScore: showSubscore === false,
                setDefaultValue: () => {
                    setValue(propertyDefaultOptions);
                    updatePropertyValue(propertyDefaultOptions);
                },
                setGenericValue: () => {
                    setGenericValueSelected((prevState) => !prevState);
                    setValue(!genericValueSelected ? propertyGenericOptions : undefined);
                    updatePropertyValue(
                        !genericValueSelected ? propertyGenericOptions : undefined,
                        !genericValueSelected,
                    );
                },
                hideUnit: true,
            }}
        >
            <FoodsCheckbox
                values={options}
                selectedItems={value}
                onChange={(selectedValues: DataListOption[]) => {
                    setValue(selectedValues);
                    updatePropertyValue(selectedValues);
                }}
                getId={(option: DataListOption) => option.id}
                getName={(option: DataListOption) => option.label}
                FormGroupProps={{
                    sx: {
                        paddingLeft: "2px",
                    },
                }}
                FormControlLabelProps={{
                    sx: {
                        gap: "3px",
                        // alignItems: "flex-start",
                        ".MuiTypography-root": {
                            ...theme.typography.body,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                        },
                    },
                }}
                CheckboxProps={{
                    disabled: !canEdit || property.readonly || genericValueSelected,
                }}
            />
        </FieldValueLayout>
    );
};
